import { getAgentUsage, type AgentUsageRecord } from "@/api/interaction-log";
import { useQuery } from "@tanstack/react-query";
import { endOfToday, subDays } from "date-fns";
import { Link } from "react-router-dom";
import { usePersonaQuery } from "@/api/personas.ts";
import { usePrivy } from "@privy-io/react-auth";
import { X } from "@/components/logos/X.tsx";
import { PersonaImageAvatar } from "../PersonaImageAvatar";

export const apiUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_API_URL_PRODUCTION
  : import.meta.env.VITE_REACT_API_URL_DEVELOPMENT;

const InteractionCard = ({
  record,
  isAdmin,
}: {
  record: AgentUsageRecord;
  isAdmin: boolean;
}) => {
  const { data: agent } = usePersonaQuery(
    usePrivy().authenticated,
    record.agent_id,
  );

  const dateToDisplay = new Date(record.timestamp);
  const localDate = dateToDisplay.toLocaleString(undefined, {
    timeZoneName: "short",
  });

  const getTextFromMetadata = (
    metadata: Record<string, any> | undefined,
  ): string | null => {
    if (!metadata) return null;
    if (metadata?.text) return metadata.text;
    if (metadata?.response?.response?.text)
      return metadata.response.response.text;
    if (metadata?.response?.text) return metadata.response.text;
    return null;
  };

  const tweetId = record.metadata?.message_id;

  const displayText =
    getTextFromMetadata(record.metadata) ||
    record.product_description ||
    "No description available";

  return (
    <div className="bg-black/20 rounded-lg px-6 py-4 border border-white/10 hover:border-primary/50 transition-all duration-200 backdrop-blur-sm">
      <div className="flex justify-between items-center gap-2">
        <div className="flex items-center gap-3">
          <PersonaImageAvatar
            className="h-8 w-8"
            altText={agent?.name || record.agent_id}
            src={
              agent?.avatar_id
                ? `${apiUrl}/get-file?url=persona-knowledge-base/${record.agent_id}/${agent.avatar_id}`
                : undefined
            }
            gender={agent?.gender}
          />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-white max-w-64 truncate">
              {
                // @ts-expect-error record probably does not have a name property
                record.name || record.agent_id
              }
            </span>
          </div>
        </div>
        <span className="text-sm text-white/60">{localDate}</span>
      </div>

      <div className="mt-4">
        <div className="flex flex-row items-center gap-x-2 mb-2">
          <h3 className="text-base font-semibold text-primary">
            {record.product_name || record.product_sku}
          </h3>
          {agent?.twitter && tweetId ? (
            <Link
              className="flex items-center"
              to={`//x.com/${agent.twitter}/status/${tweetId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View tweet"
            >
              <X fill="white" />
            </Link>
          ) : null}
        </div>
        <p className="text-sm text-white/80 whitespace-pre-wrap">
          {displayText}
        </p>
      </div>

      {isAdmin && (
        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
          <div className="text-xs text-white/60">{record.product_sku}</div>
          <div className="text-xs text-white/60">{record.record_id}</div>
        </div>
      )}
    </div>
  );
};

export const InteractionList = ({
  records,
  isAdmin,
  hiddenSkus = [
    "agent-automation-default-agent",
    "agent-automation-fast-agent",
    "agent-automation-turbo-agent",
  ],
}: {
  records?: AgentUsageRecord[];
  isAdmin: boolean;
  hiddenSkus?: string[];
}) => {
  if (!records || records.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-white/60 border border-white/10 rounded-lg bg-black/20 backdrop-blur-sm">
        No interaction records found
      </div>
    );
  }

  const filteredRecords = records.filter(
    (record) => !hiddenSkus.includes(record.product_sku),
  );

  const sortedRecords = [...filteredRecords].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return (
    <div className="space-y-4 p-6">
      {sortedRecords.map((record) => (
        <InteractionCard
          key={record.record_id}
          record={record}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
};

interface InteractionLogProps {
  agentId: string;
  startTime?: string;
  endTime?: string;
  isAdmin?: boolean;
}

const InteractionLog = ({
  agentId,
  startTime,
  endTime,
  isAdmin = false,
}: InteractionLogProps) => {
  const today = endOfToday();
  const sevenDaysAgo = subDays(today, 7);

  const start = startTime || sevenDaysAgo.toISOString();
  const end = endTime || today.toISOString();

  const { data, isLoading, error } = useQuery({
    queryKey: ["agent-usage", agentId, start, end],
    queryFn: () => getAgentUsage(agentId, start, end),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-white/60 border border-white/10 rounded-lg bg-black/20 backdrop-blur-sm">
        Loading interaction records...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-500 border border-white/10 rounded-lg bg-black/20 backdrop-blur-sm">
        Error loading interaction records. Please try again later.
      </div>
    );
  }

  return <InteractionList records={data?.records} isAdmin={isAdmin} />;
};

export default InteractionLog;
